import { currencyMinorDigits } from "./money";

// Decimal input in the English (en-US) format: "." is the decimal separator,
// "," groups thousands in exact groups of three. The result is either a valid
// canonical dot-decimal string in the caller's unit or an error code; an
// invalid or empty input never becomes 0. No value is rounded: a value with
// more fraction digits than allowed is an error, so the stored amount is
// exactly what was typed (fixes the "|| 0 becomes a free tariff" pattern, S-3,
// when forms migrate in Phase 4).

export type DecimalParseError = "empty" | "invalid" | "grouping" | "negative" | "too_many_fraction_digits" | "too_long" | "below_min" | "above_max";

export type DecimalParseResult =
  | { readonly ok: true; /** Canonical: no grouping, no leading zeros, no trailing fraction zeros ("1234.5"). */ readonly value: string; readonly number: number }
  | { readonly ok: false; readonly error: DecimalParseError };

export interface DecimalParseOptions {
  /** Required: the number of fraction digits the target field stores exactly. */
  readonly maxFractionDigits: number;
  readonly allowNegative?: boolean;
  /** Inclusive bounds as exact decimal strings ("0", "999.99"). */
  readonly min?: string;
  readonly max?: string;
  /** Accept "1,234.5" (default true); "1,5" and "12,34" are always grouping errors, never a decimal comma. */
  readonly allowGrouping?: boolean;
}

const PLAIN = /^(\d*)(?:\.(\d*))?$/;
const GROUPED = /^(\d{1,3}(?:,\d{3})+)(?:\.(\d*))?$/;
const MAX_DIGITS = 30;

function canonical(sign: "" | "-", whole: string, fraction: string): string {
  const w = whole.replace(/^0+(?=\d)/, "") || "0";
  const f = fraction.replace(/0+$/, "");
  const body = f ? `${w}.${f}` : w;
  return body === "0" ? "0" : `${sign}${body}`;
}

/** Compares canonical decimal strings exactly: -1, 0 or 1. */
export function compareDecimal(a: string, b: string): number {
  const neg = (s: string) => s.startsWith("-");
  if (neg(a) !== neg(b)) return neg(a) ? -1 : 1;
  const flip = neg(a) ? -1 : 1;
  const [aw, af = ""] = a.replace("-", "").split(".");
  const [bw, bf = ""] = b.replace("-", "").split(".");
  if (aw.length !== bw.length) return (aw.length < bw.length ? -1 : 1) * flip;
  const len = Math.max(af.length, bf.length);
  const x = aw + af.padEnd(len, "0");
  const y = bw + bf.padEnd(len, "0");
  return x === y ? 0 : (x < y ? -1 : 1) * flip;
}

function bound(value: string | undefined, what: string): string | undefined {
  if (value === undefined) return undefined;
  const parsed = parseDecimalInput(value, { maxFractionDigits: MAX_DIGITS, allowNegative: true, allowGrouping: false });
  if (!parsed.ok) throw new TypeError(`${what} ${JSON.stringify(value)} is not a decimal`);
  return parsed.value;
}

export function parseDecimalInput(text: string, options: DecimalParseOptions): DecimalParseResult {
  const { maxFractionDigits, allowNegative = false, allowGrouping = true } = options;
  if (!Number.isInteger(maxFractionDigits) || maxFractionDigits < 0) throw new TypeError("maxFractionDigits must be a non-negative integer");
  const trimmed = typeof text === "string" ? text.trim() : "";
  if (trimmed === "") return { ok: false, error: "empty" };

  const sign = trimmed.startsWith("-") ? "-" : "";
  const body = sign ? trimmed.slice(1) : trimmed;
  let whole: string;
  let fraction: string;
  const grouped = GROUPED.exec(body);
  const plain = PLAIN.exec(body);
  if (grouped && allowGrouping) {
    whole = grouped[1].replace(/,/g, "");
    fraction = grouped[2] ?? "";
  } else if (plain && (plain[1] || plain[2])) {
    whole = plain[1];
    fraction = plain[2] ?? "";
  } else {
    return { ok: false, error: body.includes(",") ? "grouping" : "invalid" };
  }
  if (whole.length + fraction.length > MAX_DIGITS) return { ok: false, error: "too_long" };
  if (fraction.replace(/0+$/, "").length > maxFractionDigits) return { ok: false, error: "too_many_fraction_digits" };

  const value = canonical(sign, whole, fraction);
  if (value.startsWith("-") && !allowNegative) return { ok: false, error: "negative" };
  const min = bound(options.min, "min");
  const max = bound(options.max, "max");
  if (min !== undefined && compareDecimal(value, min) < 0) return { ok: false, error: "below_min" };
  if (max !== undefined && compareDecimal(value, max) > 0) return { ok: false, error: "above_max" };
  return { ok: true, value, number: Number(value) };
}

/**
 * Exact integer minor units of a canonical decimal for a currency:
 * ("12.5", "USD") -> 1250n. Fails instead of rounding when the value has
 * more fraction digits than the currency (ISO 4217 exponent).
 */
export function decimalToMinorUnits(value: string, currency: string): { readonly ok: true; readonly minor: bigint } | { readonly ok: false; readonly error: "invalid" | "too_many_fraction_digits" } {
  const digits = currencyMinorDigits(currency);
  const parsed = parseDecimalInput(value, { maxFractionDigits: MAX_DIGITS, allowNegative: true, allowGrouping: false });
  if (!parsed.ok) return { ok: false, error: "invalid" };
  const negative = parsed.value.startsWith("-");
  const [w, f = ""] = parsed.value.replace("-", "").split(".");
  if (f.length > digits) return { ok: false, error: "too_many_fraction_digits" };
  const minor = BigInt(w + f.padEnd(digits, "0"));
  return { ok: true, minor: negative ? -minor : minor };
}
